/** Canvas element-anchored overlay repositioning
 * (VISTOERA_CANVAS_GLOBAL_NODE_ANCHORED_OVERLAY_PORTAL_POSITIONING_PAN_ZOOM_SCROLL_PROVIDER_SKILL_AND_ALL_FLOATING_CONTROLS_P0_V1).
 *
 * React Flow pan/zoom applies CSS transforms on `.react-flow__viewport`.
 * Ant Design Select/Dropdown/Tooltip popups portal to `document.body` and
 * rely on `@rc-component/trigger`, which re-aligns on scroll/resize — but
 * transform-only viewport movement does NOT emit scroll events. This module
 * provides a single, canvas-scoped reposition pulse that rc-trigger listens
 * to, driven only while at least one anchored overlay is open. */

import { useStoreApi } from '@xyflow/react'
import { createContext, useCallback, useContext, useEffect, useRef, type Context } from 'react'

export function triggerAnchoredOverlayReposition(): void {
  if (typeof window === 'undefined') return
  // rc-trigger registers capture-phase scroll/resize listeners on window.
  window.dispatchEvent(new Event('scroll', { bubbles: true }))
  window.dispatchEvent(new Event('resize'))
}

type RegisterOpenLayer = () => () => void

const noopRegister: RegisterOpenLayer = () => () => {}

export interface CanvasFloatingLayerContextValue {
  registerOpenLayer: RegisterOpenLayer
}

export const CanvasFloatingLayerContext: Context<CanvasFloatingLayerContextValue> = createContext({
  registerOpenLayer: noopRegister,
})

/** Subscribe a single anchored overlay lifecycle to the shared transform tracker. */
export function useCanvasAnchoredOverlayOpen(open: boolean): void {
  const { registerOpenLayer } = useContext(CanvasFloatingLayerContext)
  useEffect(() => {
    if (!open) return
    return registerOpenLayer()
  }, [open, registerOpenLayer])
}

/** Imperative hook for custom overlays that manage their own reposition fn. */
export function useCanvasAnchoredReposition(open: boolean, reposition: () => void): void {
  const store = useStoreApi()
  const repositionRef = useRef(reposition)
  repositionRef.current = reposition

  useEffect(() => {
    if (!open) return
    let rafId = 0
    let lastTransform = ''
    const tick = () => {
      const transform = store.getState().transform
      const key = transform.join(',')
      if (key !== lastTransform) {
        lastTransform = key
        repositionRef.current()
        triggerAnchoredOverlayReposition()
      }
      rafId = requestAnimationFrame(tick)
    }
    lastTransform = store.getState().transform.join(',')
    rafId = requestAnimationFrame(tick)
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [open, store])
}

export function useCanvasFloatingLayerRegistrar(): RegisterOpenLayer {
  const store = useStoreApi()
  const openCountRef = useRef(0)
  const rafRef = useRef(0)
  const lastTransformRef = useRef('')

  const stopLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = 0
    }
  }, [])

  const startLoop = useCallback(() => {
    if (rafRef.current) return
    const tick = () => {
      if (openCountRef.current <= 0) {
        stopLoop()
        return
      }
      const transform = store.getState().transform
      const key = transform.join(',')
      if (key !== lastTransformRef.current) {
        lastTransformRef.current = key
        triggerAnchoredOverlayReposition()
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [stopLoop, store])

  return useCallback(() => {
    openCountRef.current += 1
    lastTransformRef.current = store.getState().transform.join(',')
    startLoop()
    return () => {
      openCountRef.current = Math.max(0, openCountRef.current - 1)
      if (openCountRef.current === 0) stopLoop()
    }
  }, [startLoop, stopLoop, store])
}
